import React, { useState } from "react";
import { Instagram, Youtube, MapPin, Edit2, Plus, CheckCircle2, Users, Bookmark } from "lucide-react";
import { Button } from "@/components/ui/button";
import { EditInfluencerModal } from "./EditInfluencerModal";
import { AddToCampaignModal } from "@/components/AddToCampaignModal";

export default function ProfileHeader({ influencer, onUpdated }: { influencer: any; onUpdated?: () => void }) {
  const [editOpen, setEditOpen] = useState(false);
  const [addToCampaignOpen, setAddToCampaignOpen] = useState(false);

  // Add null check for influencer
  if (!influencer) {
    return (
      <section className="flex items-center justify-center h-32 bg-white rounded-xl shadow p-6">
        <div className="text-gray-500">Loading influencer data...</div>
      </section>
    );
  }

  const igHandle = influencer._db?.handle || influencer.handle?.replace(/^@/, "");
  const igUrl = igHandle ? `https://instagram.com/${igHandle}` : undefined;
  const ytHandle = influencer.platforms?.includes?.("YouTube") ? influencer.handle : null;

  // Support both flat and _db fields as fallback
  const tags = Array.isArray(influencer.tags)
    ? influencer.tags
    : influencer._db?.categories || [];

  const formatAddedDate = (dateStr?: string) => {
    if (!dateStr) return "Added on Not set";
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return `Added on ${dateStr}`;
    return "Added on " + date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatFollowers = (count?: number | null) => {
    if (!count) return "0";
    if (count >= 1_000_000) return (count / 1_000_000).toFixed(1).replace(/\.0$/, '') + "M";
    if (count >= 1_000) return (count / 1_000).toFixed(0) + "K";
    return count.toString();
  };

  return (
    <section className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 bg-white rounded-2xl border border-slate-200 p-8 shadow-sm">
      <div className="flex flex-col md:flex-row md:items-start gap-6 flex-1">
        {/* Left Side: Photo */}
        <div className="w-32 h-32 rounded-full border border-slate-200 flex items-center justify-center bg-slate-50 overflow-hidden flex-shrink-0">
          {influencer.profilePhoto?.startsWith('http') ? (
            <img src={influencer.profilePhoto} alt={influencer.realName} className="w-full h-full object-cover" />
          ) : (
            <span className="text-slate-400 font-bold text-3xl">
              {influencer.realName?.charAt(0).toUpperCase() || "?"}
            </span>
          )}
        </div>

        {/* Center Side: Info */}
        <div className="flex-1 space-y-2">
          <div className="flex items-center gap-2">
            <h2 className="text-3xl font-bold text-slate-900 tracking-tight">{influencer.realName}</h2>
            <CheckCircle2 className="w-6 h-6 text-white fill-[#A655F8]" />
          </div>

          <div className="flex flex-wrap items-center gap-4">
            {igUrl ? (
              <a href={igUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-slate-600 hover:text-[#A655F8] transition-colors font-semibold text-[15px] mt-1">
                <img src="/icons/instagram-logos/GradientGlyph/Instagram_Glyph_Gradient.png" alt="Instagram" className="w-5 h-5 object-contain" />
                <span>@{igHandle}</span>
              </a>
            ) : (
              <span className="text-slate-400 text-sm">No instagram account</span>
            )}
            {ytHandle && (
              <span className="flex items-center gap-1.5 text-slate-600 font-semibold text-[15px] mt-1">
                <Youtube className="w-5 h-5 text-red-500" />
                <span>{ytHandle}</span>
              </span>
            )}
          </div>

          <div className="flex flex-wrap gap-2 pt-1.5">
            {tags.map((tag: string, idx: number) => (
              <span key={idx} className="px-3 py-1 rounded-xl bg-[#F3E8FF] text-[#A655F8] text-[13px] font-medium">
                #{tag}
              </span>
            ))}
          </div>

          {/* Stats Row */}
          <div className="flex flex-wrap items-center gap-x-8 gap-y-4 pt-2 mt-2">
            {/* Stat 1: Followers */}
            <div className="flex items-start gap-2.5">
              <Users className="w-5 h-5 text-slate-400 mt-0.5" />
              <div>
                <div className="text-[16px] font-bold text-slate-900 leading-tight">
                  {formatFollowers(influencer._db?.followers_count ?? influencer.followers_count)}
                </div>
                <div className="text-[14px] text-[#44464F] font-medium">Followers</div>
              </div>
            </div>

            {/* Divider */}
            <div className="hidden sm:block h-10 w-px bg-slate-200" />

            {/* Stat 2: Location */}
            <div className="flex items-start gap-2.5">
              <MapPin className="w-5 h-5 text-slate-400 mt-0.5" />
              <div>
                <div className="text-[16px] font-bold text-slate-900 leading-tight">
                  {influencer.location || "Not specified"}
                </div>
                <div className="text-[14px] text-[#44464F] font-medium">Location</div>
              </div>
            </div>

            {/* Divider */}
            <div className="hidden sm:block h-10 w-px bg-slate-200" />

            {/* Stat 3: Category */}
            <div className="flex items-start gap-2.5">
              <Bookmark className="w-5 h-5 text-slate-400 mt-0.5" />
              <div>
                <div className="text-[16px] font-bold text-slate-900 leading-tight truncate max-w-[180px]">
                  {tags.join(", ") || "Content Creator"}
                </div>
                <div className="text-[14px] text-[#44464F] font-medium">Category</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side: Actions */}
      <div className="flex flex-col items-stretch gap-2 min-w-[200px] w-full lg:w-auto shrink-0 self-stretch lg:self-center justify-between py-1">
        <Button
          className="w-full bg-[#A655F8] text-white rounded-xl py-4 px-4 h-auto flex items-center justify-center gap-3 transition-colors"
          onClick={() => setAddToCampaignOpen(true)}
        >
          <Plus className="w-4 h-4" />
          <div className="text-left text-sm font-semibold leading-tight">
            Add to Campaign
          </div>
        </Button>
        <Button
          variant="outline"
          className="w-full bg-white hover:bg-slate-50 text-slate-700 border-slate-300 rounded-xl py-4 px-4 h-auto flex items-center justify-center gap-3 transition-colors"
          onClick={() => setEditOpen(true)}
        >
          <Edit2 className="w-4 h-4 text-slate-500" />
          <div className="text-left text-sm font-semibold leading-tight">
            Edit Influencer
          </div>
        </Button>
        <div className="text-[#44464F] text-sm font-medium pr-1 w-full text-center lg:text-right mt-1">
          {formatAddedDate(influencer._db?.created_at || influencer.created_at)}
        </div>
      </div>

      <EditInfluencerModal
        open={editOpen}
        onOpenChange={setEditOpen}
        influencer={{
          id: influencer._db?.id || influencer.id,
          name: influencer.realName,
          handle: influencer.handle.replace(/^@/, ""),
          platform: influencer._db?.platform || influencer.platform,
          location: influencer.location,
          engagement_rate: influencer.engagementRate,
          followers_count: influencer._db?.followers_count ?? influencer.followers_count ?? null,
          categories: tags,
        }}
        onSaved={onUpdated ?? (() => { })}
      />
      <AddToCampaignModal
        open={addToCampaignOpen}
        onOpenChange={setAddToCampaignOpen}
        influencers={[
          {
            id: influencer._db?.id || influencer.id,
            realName: influencer.realName || influencer.name || "Influencer",
            handle: influencer.handle || influencer._db?.handle || "",
            profilePhoto: influencer.profilePhoto || influencer._db?.profile_pic_url || null,
            platform: influencer.platform || influencer._db?.platform || "Instagram",
            followers: influencer._db?.followers_count ?? influencer.followers_count ?? influencer.followers?.instagram ?? null,
            engagementRate: influencer.engagementRate ?? influencer._db?.engagement_rate ?? null,
            videoViews: influencer._db?.video_view_count ?? influencer.video_view_count ?? null,
            avgLikes: influencer._db?.avg_likes ?? influencer.avg_likes ?? null,
            category: tags?.[0] || influencer.category || "Influencer",
            verified: influencer._db?.verified ?? influencer.verified ?? true,
          },
        ]}
        onAssigned={onUpdated}
      />
    </section>
  );
}
